import { injectable } from 'tsyringe';
import { insightsClient } from '@/shared/InsightsApiClient';
import type { AvailableRanges, ScorecardSnapshot, ScorecardView, SavedRange } from './InsightsTypes';

/**
 * Data source for the insights scorecard. Reads precomputed (or computed-on-
 * demand) scorecards and manages the signed-in user's saved custom ranges via
 * the insights-api. Identity (tenant + user) is resolved server-side from the
 * forwarded bearer token, so callers pass only range + view.
 */
export interface InsightsGateway {
    fetchScorecard(rangeKey: string, view: ScorecardView): Promise<ScorecardSnapshot>;
    fetchRanges(): Promise<AvailableRanges>;
    saveRange(label: string, startDate: string, endDate: string): Promise<SavedRange>;
    deleteRange(id: string): Promise<void>;
}

@injectable()
export class InsightsApiGateway implements InsightsGateway {
    private readonly api = insightsClient.insightsApi;

    fetchScorecard(rangeKey: string, view: ScorecardView): Promise<ScorecardSnapshot> {
        return this.api.getScorecard({ range: rangeKey, view });
    }

    fetchRanges(): Promise<AvailableRanges> {
        return this.api.getRanges();
    }

    saveRange(label: string, startDate: string, endDate: string): Promise<SavedRange> {
        return this.api.postRanges({ saveRangeBody: { label, startDate, endDate } });
    }

    async deleteRange(id: string): Promise<void> {
        await this.api.deleteRangesById({ id });
    }
}
